import * as THREE from 'three';
import { cloth, emis, metal, std } from '../materials.ts';
import { G, M, V, glowSprite, limb } from '../prims.ts';
import { kit } from './era4-kit.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * Vigía de ceniza (era V, docs/CRIATURAS.md «Era V · Somnhold»): an angel of the Order that fell with Somnhold, as
 * the titan dreams it now. Taller than a man, in plate gone dull and dark, scored and dented, with what is left of
 * the Order's crimson hanging off it in rags; under a closed great helm, no face: only a slit of pale light. A
 * broken halo of black iron behind its head with the last of a fire caught in the break. Great wings of ash-grey
 * feathers, wider than it is tall, that shed embers as they beat. Its hands are gauntlets with long talons: it
 * falls on a hero and tears the light out of them (`steal`, a mote of pale light that passes from its claw to its
 * breast).
 *
 * It flies: the hover is its rest pose (`WATCHER_HOVER`; the dive drops it to the ground and the rise brings it
 * back; `die` drops it). Bones: body (hips), chest, head, armL/foreL, armR/foreR, wingL/wingLtip and wingR/wingRtip
 * (the wing from the shoulder blade to the wrist, then the hand with the long flight feathers), legL/shinL,
 * legR/shinR (hanging), and `steal` (the mote of light, built tiny at its breast). Faces +Z with its right on +X.
 * Opaque shared materials, the pale light and the embers self-lit, plus glow sprites (no lights).
 */

export const WATCHER_HOVER = 1.35;
/** How small the mote of stolen light is built (the steal clip scales it by about 1 / MOTE_REST). */
export const MOTE_REST = 0.02;
/** Where the mote rests, at its breast (model units, before the hover). */
export const MOTE_AT = V(0, 1.42, 0.24);

export const ashWatcher = (o?: BaseOpts): Model => {
  const c = begin(o, 0.62, 8209);
  const { g, rng } = c;
  const k4 = kit(rng);
  g.position.y = WATCHER_HOVER;

  // Plate gone dull (cool, light enough to read from the table camera), its dark lames and the halo's iron, the
  // Order's crimson rotted dark, the ash of the wings and its paler edges, the pale light, the embers.
  const plate = metal('#5b5f69', { roughness: 0.55, metalness: 0.45, bumpScale: 0.03 });
  const plateD = metal('#2c2f36', { roughness: 0.6, metalness: 0.45, bumpScale: 0.03 });
  const crimson = cloth('#5e1419', { side: THREE.DoubleSide });
  const ash = std('#3c3c45', { roughness: 0.95, flatShading: true, side: THREE.DoubleSide, bumpScale: 0.03 });
  const ashL = std('#73727e', { roughness: 0.95, flatShading: true, side: THREE.DoubleSide, bumpScale: 0.03 });
  const light = emis('#f4f7ff', '#b9c8ff', 2.8);
  const ember = emis('#ffb27a', '#ff5a1f', 2.2);

  /** A feather: a flat blade from `at` along `dir`, `len` long, `w` wide. */
  const feather = (parent: THREE.Object3D, mat: THREE.Material, at: THREE.Vector3, dir: THREE.Vector3, len: number, w: number, roll = 0): THREE.Mesh => {
    const d = dir.clone().normalize();
    const geo = new THREE.ConeGeometry(w, len, 4, 1);
    geo.translate(0, len / 2, 0);
    geo.scale(1, 1, 0.16);
    const m = M(geo, mat, parent);
    m.position.copy(at);
    m.quaternion.setFromUnitVectors(V(0, 1, 0), d);
    m.rotateY(roll);
    return m;
  };

  /* ---------- legs: hanging, armoured, the pointed sabatons toes down ---------- */
  const legs: Record<string, THREE.Object3D> = {};
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const legB = joint(g, V(k * 0.15, 1.0, 0), `leg${s}`);
    const hip = legB.abs;
    limb(hip, V(k * 0.15, 1.02, 0), V(k * 0.16, 0.6, 0.04), 0.11, 0.085, plate, 7);
    const shinB = joint(hip, V(k * 0.16, 0.58, 0.04), `shin${s}`);
    const lo = shinB.abs;
    k4.swell(lo, plateD, k * 0.16, 0.58, 0.1, 0.075, 0.07, 0.06, 6, 4);
    limb(lo, V(k * 0.16, 0.56, 0.04), V(k * 0.16, 0.18, 0.0), 0.085, 0.065, plate, 7);
    const toe = M(new THREE.ConeGeometry(0.06, 0.28, 4), plateD, lo);
    toe.position.set(k * 0.16, 0.08, 0.04);
    toe.rotation.set(Math.PI - 0.5, Math.PI / 4, 0);
    toe.scale.set(1, 1, 0.6);
    legs[`leg${s}`] = legB.p;
    legs[`shin${s}`] = shinB.p;
  }

  /* ---------- hips: faulds, and the rags of the Order's crimson ---------- */
  const bodyB = joint(g, V(0, 1.02, 0), 'body');
  const body = bodyB.abs;
  const fauld = M(new THREE.CylinderGeometry(0.22, 0.29, 0.26, 9), plateD, body);
  fauld.position.set(0, 1.0, 0);
  for (const z of [1, -1]) {
    for (let i = 0; i < 3; i++) {
      const p = k4.panel(body, V(-0.1 + i * 0.1, 1.1, z * 0.21), 0.12, 0.72 - (i % 2) * 0.18, 0.04, z, crimson, 0.6);
      p.rotation.set(z * 0.08, (i - 1) * 0.15, 0);
    }
  }

  /* ---------- chest: a breastplate, pauldrons, a gorget; a seam of the fire under the plate ---------- */
  const chestB = joint(body, V(0, 1.2, 0), 'chest');
  const chest = chestB.abs;
  const cuirass = k4.swell(chest, plate, 0, 1.42, 0.02, 0.27, 0.3, 0.2, 9, 6);
  cuirass.rotation.x = -0.08;
  k4.swell(chest, plateD, 0, 1.2, 0.0, 0.23, 0.12, 0.17, 8, 4);
  // A ridge down the breastplate and the Order's diamond on it, scraped half away.
  M(new THREE.BoxGeometry(0.025, 0.36, 0.03), plateD, chest).position.set(0, 1.44, 0.21);
  const dia = M(new THREE.OctahedronGeometry(0.07, 0), crimson, chest);
  dia.position.set(0, 1.5, 0.215);
  dia.scale.set(0.8, 1.1, 0.25);
  // The breastplate is split: the fire it fell with shows through.
  k4.crack(chest, [V(-0.12, 1.6, 0.17), V(-0.06, 1.48, 0.21), V(-0.09, 1.36, 0.21), V(-0.03, 1.26, 0.18)], 0.008, ember, 0.03);
  k4.swell(chest, plateD, 0, 1.7, 0.0, 0.13, 0.06, 0.12, 8, 3);
  for (const k of [-1, 1]) {
    const pa = M(new THREE.SphereGeometry(0.15, 8, 5, 0, Math.PI * 2, 0, Math.PI / 2), plate, chest);
    pa.position.set(k * 0.29, 1.6, 0);
    pa.rotation.z = -k * 0.45;
    pa.scale.set(1, 0.8, 1.05);
    const pl = M(new THREE.SphereGeometry(0.16, 8, 3, 0, Math.PI * 2, 0, Math.PI / 4), plateD, chest);
    pl.position.set(k * 0.31, 1.53, 0);
    pl.rotation.z = -k * 0.55;
  }
  // A rag of crimson off the left shoulder.
  k4.panel(chest, V(-0.3, 1.66, -0.06), 0.18, 0.6, 0.05, -1, crimson, 0.6).rotation.set(-0.1, 0.4, 0.15);
  // The mote of stolen light, built tiny at the breast.
  const stealB = joint(chest, MOTE_AT, 'steal');
  {
    const mg = G(stealB.abs, MOTE_AT.x, MOTE_AT.y, MOTE_AT.z);
    mg.scale.setScalar(MOTE_REST);
    M(new THREE.IcosahedronGeometry(0.09, 1), light, mg);
    for (let i = 0; i < 5; i++) {
      const d = V(rng() - 0.5, rng() - 0.5, rng() - 0.5).normalize();
      k4.shard(mg, light, V(0, 0, 0), d, 0.2 + rng() * 0.1, 0.02);
    }
  }

  /* ---------- head: a closed great helm, a slit of pale light; the broken halo behind ---------- */
  const headB = joint(chest, V(0, 1.72, 0.0), 'head');
  const head = headB.abs;
  const helm = M(new THREE.CylinderGeometry(0.13, 0.135, 0.28, 9), plate, head);
  helm.position.set(0, 1.88, 0.02);
  const dome = M(new THREE.SphereGeometry(0.13, 9, 5, 0, Math.PI * 2, 0, Math.PI / 2), plate, head);
  dome.position.set(0, 2.02, 0.02);
  dome.scale.set(1, 0.7, 1);
  // A ridge over the crown, and the brow band.
  M(new THREE.BoxGeometry(0.02, 0.1, 0.27), plateD, head).position.set(0, 2.06, 0.02);
  const band = M(new THREE.TorusGeometry(0.135, 0.012, 3, 12), plateD, head);
  band.position.set(0, 1.93, 0.02);
  band.rotation.x = Math.PI / 2;
  // The slit: no face in there, only the light.
  const slit = M(new THREE.BoxGeometry(0.17, 0.022, 0.04), light, head);
  slit.position.set(0, 1.9, 0.14);
  M(new THREE.BoxGeometry(0.02, 0.09, 0.04), light, head).position.set(0, 1.84, 0.145);
  glowSprite(head, '#dbe6ff', 0.38, V(0, 1.9, 0.18), 0.95);
  // The halo: a ring of black iron, broken, the fire caught in the break.
  const halo = G(head, 0, 2.02, -0.2);
  halo.rotation.set(-0.25, 0, 0.3);
  M(new THREE.TorusGeometry(0.3, 0.022, 4, 20, Math.PI * 1.55), plateD, halo).rotation.z = 0.5;
  for (let i = 0; i < 8; i++) {
    const a = 0.5 + (i / 8) * Math.PI * 1.55;
    const sp = M(new THREE.ConeGeometry(0.02, 0.1, 4), plateD, halo);
    sp.position.set(Math.cos(a) * 0.33, Math.sin(a) * 0.33, 0);
    sp.rotation.z = a - Math.PI / 2;
  }
  for (const a of [0.5, 0.5 + Math.PI * 1.55]) {
    M(new THREE.SphereGeometry(0.028, 5, 4), ember, halo).position.set(Math.cos(a) * 0.3, Math.sin(a) * 0.3, 0);
  }
  glowSprite(halo, '#ff6a2a', 0.3, V(Math.cos(0.5) * 0.3, Math.sin(0.5) * 0.3, 0), 0.7);

  /* ---------- arms: plate, the gauntlets ending in long talons ---------- */
  const arms: Record<string, THREE.Object3D> = {};
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const armB = joint(chest, V(k * 0.3, 1.58, 0), `arm${s}`);
    const ar = armB.abs;
    limb(ar, V(k * 0.32, 1.56, 0), V(k * 0.38, 1.2, 0.02), 0.08, 0.065, plate, 7);
    const foreB = joint(ar, V(k * 0.38, 1.2, 0.02), `fore${s}`);
    const fo = foreB.abs;
    k4.swell(fo, plateD, k * 0.38, 1.2, 0.0, 0.07, 0.065, 0.065, 6, 4);
    limb(fo, V(k * 0.38, 1.18, 0.03), V(k * 0.39, 0.88, 0.1), 0.068, 0.058, plate, 7);
    // The gauntlet's flared cuff, the fist, and four long talons hooked forward.
    M(new THREE.CylinderGeometry(0.08, 0.06, 0.08, 7), plateD, fo).position.set(k * 0.39, 0.93, 0.09);
    k4.swell(fo, plateD, k * 0.39, 0.84, 0.12, 0.055, 0.06, 0.05, 6, 4);
    for (let i = 0; i < 4; i++) {
      const x = k * (0.36 + i * 0.022);
      const a = V(x, 0.8, 0.15), m = V(x + k * 0.005, 0.68, 0.22), e = V(x + k * 0.01, 0.6, 0.31);
      limb(fo, a, m, 0.014, 0.011, plateD, 4);
      limb(fo, m, e, 0.011, 0.002, plate, 4);
    }
    arms[`arm${s}`] = armB.p;
    arms[`fore${s}`] = foreB.p;
  }

  /* ---------- wings: from the shoulder blades, great and ash-grey, shedding embers ---------- */
  const wings: Record<string, THREE.Object3D> = {};
  const sparks: { s: THREE.Sprite; p: THREE.Vector3; ph: number }[] = [];
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const X = (x: number, y: number, z: number): THREE.Vector3 => V(k * x, y, z);
    const root = X(0.13, 1.56, -0.18), wrist = X(0.95, 1.95, -0.32);
    const wingB = joint(chest, root, `wing${s}`);
    const wi = wingB.abs;
    // The arm of the wing: a heavy bone of ash, and the coverts over it in rows.
    limb(wi, root, wrist, 0.07, 0.045, ash, 5);
    k4.swell(wi, ash, k * 0.3, 1.68, -0.24, 0.16, 0.13, 0.08, 6, 4);
    for (let row = 0; row < 3; row++) {
      for (let i = 0; i < 6; i++) {
        const u = (i + 0.5) / 6;
        const at = root.clone().lerp(wrist, u).add(V(0, -0.03 - row * 0.05, -0.02 - row * 0.02));
        const len = 0.28 + row * 0.2 + (1 - u) * 0.1;
        feather(wi, (row + i) % 3 === 0 ? ashL : ash, at, X(0.12 + u * 0.3, -1, -0.12), len, 0.07 + row * 0.01, k * 0.2);
      }
    }
    // The secondaries off the arm's trailing edge, long, hanging down and back.
    for (let i = 0; i < 6; i++) {
      const u = (i + 0.5) / 6;
      const at = root.clone().lerp(wrist, u).add(V(0, -0.15, -0.05));
      feather(wi, i % 2 ? ash : ashL, at, X(0.25 * u, -1, -0.25), 0.95 - u * 0.15, 0.09, k * 0.15);
    }
    const tipB = joint(wi, wrist, `wing${s}tip`);
    const tp = tipB.abs;
    k4.swell(tp, ash, wrist.x, wrist.y, wrist.z, 0.07, 0.06, 0.06, 6, 4);
    // The primaries: long flight feathers fanned from the wrist, out and down, their tips paler and smouldering.
    for (let i = 0; i < 8; i++) {
      const u = i / 7;
      const dir = X(Math.cos(0.55 - u * 1.65), Math.sin(0.55 - u * 1.65), -0.18 - u * 0.1);
      const len = 1.15 - Math.abs(u - 0.25) * 0.5;
      feather(tp, i % 2 ? ash : ashL, wrist.clone().add(X(0.02, -0.02 * i, -0.01 * i)), dir, len, 0.085, k * 0.1);
      const end = wrist.clone().addScaledVector(dir.clone().normalize(), len * 0.92);
      if (i % 2 === 0) {
        M(new THREE.SphereGeometry(0.022, 4, 3), ember, tp).position.copy(end);
        sparks.push({ s: glowSprite(tp, '#ff6a2a', 0.14, end, 0.8, true), p: end, ph: rng() });
      }
    }
    // The coverts over the hand.
    for (let i = 0; i < 4; i++) feather(tp, ashL, wrist.clone().add(X(-0.05, 0.02, 0)), X(0.8, 0.2 - i * 0.25, -0.15), 0.4, 0.08, k * 0.2);
    wings[`wing${s}`] = wingB.p;
    wings[`wing${s}tip`] = tipB.p;
  }
  // Ash and embers falling off it.
  const fall: { s: THREE.Sprite; x: number; z: number; ph: number }[] = [];
  for (let i = 0; i < 5; i++) fall.push({ s: glowSprite(body, i % 2 ? '#ff6a2a' : '#ff8a4a', 0.1, V(0, 0, 0), 0.8, true), x: (rng() - 0.5) * 1.6, z: -0.2 - rng() * 0.3, ph: i / 5 });

  const t = (n: number): void => {
    for (const s of sparks) {
      const q = (s.ph + n * 0.31) % 1;
      s.s.position.set(s.p.x, s.p.y - q * 0.25, s.p.z - q * 0.1);
      s.s.material.opacity = 0.85 * (1 - q) * (q < 0.1 ? q / 0.1 : 1);
    }
    for (const e of fall) {
      const q = (e.ph + n * 0.21) % 1;
      e.s.position.set(e.x + Math.sin(q * 6 + e.ph * 9) * 0.05, 1.7 - q * 1.2, e.z - q * 0.2);
      e.s.material.opacity = 0.8 * (1 - q) * (q < 0.08 ? q / 0.08 : 1);
    }
  };
  t(0);
  return done(c, WATCHER_HOVER + 2.15, t, undefined, bonesOf(c, {
    body: bodyB.p, chest: chestB.p, head: headB.p, armL: arms.armL!, foreL: arms.foreL!, armR: arms.armR!, foreR: arms.foreR!,
    ...wings, ...legs, steal: stealB.p,
  }));
};
